import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Divider } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 0px',
    maxWidth: 980,
    margin: '0 auto',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    textAlign: 'left',

    [theme.breakpoints.down('sm')]: {
      padding: '10px 8px 0px',
    },
  },
  hero: {
    padding: '18px 18px 14px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background:
      'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.35), rgba(10,14,30,0.96))',
    boxShadow: '0 0 24px rgba(77,121,255,0.14)',
    marginBottom: 18,

    [theme.breakpoints.down('sm')]: {
      padding: '10px 10px 10px',
      marginBottom: 10,
    },
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '2.0rem',
    fontWeight: 900,
    letterSpacing: -0.4,
    lineHeight: 1.1,
    textShadow: '0 0 16px rgba(77, 121, 255, 0.55)',
  },
  subtitle: {
    marginTop: 8,
    color: '#cfd8ff',
    lineHeight: 1.6,
    fontSize: 13,
    maxWidth: 860,
    textAlign: 'left',
  },
  primaryButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: 10,
    padding: '8px 12px',
    '&:hover': { backgroundColor: '#3e61cc' },
    [theme.breakpoints.down('sm')]: {
      fontSize: 11,
      padding: '6px 10px',
    },
  },
  section: {
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.22)',
    background: 'rgba(10, 14, 30, 0.82)',
    overflow: 'hidden',
    padding: 16,
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
    color: '#e5e7ff',
    marginBottom: 10,
    textAlign: 'left',
  },
  body: {
    fontSize: 13,
    color: '#e8f5ff',
    lineHeight: 1.65,
    whiteSpace: 'pre-wrap',
    textAlign: 'left',
  },
  step: {
    borderRadius: 14,
    border: '1px solid rgba(255,255,255,0.10)',
    background: 'rgba(5, 8, 20, 0.68)',
    padding: 14,
    marginBottom: 10,
  },
  stepTitle: {
    fontSize: 14,
    fontWeight: 900,
    color: '#ffffff',
    marginBottom: 6,
  },
  callout: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(5, 8, 20, 0.88)',
    border: '1px solid rgba(158, 247, 197, 0.28)',
    color: '#e8f5ff',
    fontSize: 13,
    lineHeight: 1.65,
    textAlign: 'left',
  },
  note: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(5, 8, 20, 0.88)',
    border: '1px solid rgba(255, 180, 80, 0.35)',
    color: '#e8f5ff',
    fontSize: 13,
    lineHeight: 1.65,
    textAlign: 'left',
  },
  divider: {
    backgroundColor: 'rgba(255,255,255,0.10)',
    margin: '14px 0',
  },
}));

const useCases = [
  {
    title: 'Real-time narrative discovery',
    goal: 'Find tokens generating on-chain stories right now.',
    problem: 'You want to know where on-chain activity is happening this hour — not what trended yesterday or what has the most accumulated attention. You want live signal.',
    steps: '1. Open /price.\n2. Set the Trend picker to "1H."\n3. Click the Stories column header to sort descending.\n4. The tokens at the top of the table are the ones where the agent created the most stories in the last sixty minutes. These are active on-chain events — whale movements, cluster formations, surge detections, liquidity shifts — happening now.\n5. Click any token to open its detail page and read the stories. The narrative will tell you what kind of activity the agent observed and whether it looks like accumulation, distribution, structural change, or anomaly.',
    why: 'The 1H Stories count is the tightest lens the screener offers. It reflects agent output from the last sixty minutes, refreshed via server-side ClickHouse countIf() queries on a 10-minute cache cycle. When you sort by this column, you are looking at a near-real-time ranking of narrative attention across the full 7,000+ token coverage universe. Tokens that appear at the top of this list are where on-chain evidence is currently accumulating — before price has necessarily reacted, before social media has picked up the signal, and before other screeners even know to look.\n\nThis is like switching from a daily weather forecast to a live storm-tracking radar. Both use the same data sources, but the live view shows you where the energy is concentrated right now, not where it was twelve hours ago.',
    who: 'Intraday traders scanning for real-time setups. Alert builders who want to catch narrative events as they form. Researchers who want to watch the agent\'s attention in real time.',
  },
  {
    title: 'Daily conviction scan',
    goal: 'Find tokens where the AI formed a directional view today.',
    problem: 'You do not have time to read hundreds of individual stories. You want the tokens where the agent has already done the synthesis work — where enough converging evidence accumulated that the agent wrote a structured LONG, SHORT, or AVOID brief.',
    steps: '1. Set the Trend picker to "24H" (the default).\n2. Click the Theses column header to sort descending.\n3. Scan the top 10–20 tokens. Each one with a non-zero Theses count has at least one active thesis generated in the last 24 hours.\n4. Click any token to open its detail page. Read the thesis direction (LONG / SHORT / AVOID), the conviction score, the narrative explanation, and especially the entry signal (what to watch for) and the invalidation condition (what kills the thesis).\n5. Cross-reference with the contributing stories if you want to verify the evidence chain yourself.',
    why: 'Theses are the agent\'s highest-conviction output. They are generated only when the convergence rule engine detects multiple independent on-chain signals pointing in the same direction — patterns like whale accumulation followed by cluster formation, or multiple whale addresses independently buying the same token, or a hot-links surge confirmed by a breakout event. A 24H window is the natural period for thesis generation because convergence takes time: the agent needs multiple stories to accumulate before the rules fire.\n\nSorting by Theses descending under 24H Trend is the daily conviction scan. The tokens at the top are the ones where the agent has the most structured evidence for a directional view. This is the difference between reading a stream of police reports (stories) and reading the detective\'s case summary (thesis) — the summary tells you who the suspects are and why.',
    who: 'Swing traders running a morning scan. Portfolio managers reviewing the agent\'s overnight output. Anyone who wants structured conviction rather than raw observation.',
  },
  {
    title: 'Narrative + conviction filter',
    goal: 'Find tokens with BOTH story activity AND a thesis — the highest-signal combination.',
    problem: 'Stories alone can be noise. A single HOTLINKS spike or a thin-liquidity surge does not necessarily mean anything actionable. Theses alone are valuable, but you want to see whether narrative activity is still building or has gone quiet since the thesis was generated. You want the tokens where both layers are active.',
    steps: '1. Set the Trend picker to "24H."\n2. Sort by Theses descending.\n3. Now scan the Stories column on the same rows. Look for tokens where both columns show non-zero numbers — ideally where both are elevated.\n4. These tokens have the full AI coverage stack: the agent is actively generating stories (raw observations are ongoing) AND has formed a structured thesis (the evidence converged into a directional view).\n5. Click through to read both. Check whether the recent stories are reinforcing the thesis or contradicting it. A thesis backed by fresh, confirmatory stories is a stronger signal than a thesis whose contributing stories have gone silent.',
    why: 'Stories and Theses are two stages of the same pipeline. Stories are the raw observations. Theses are the synthesis that requires convergence — multiple stories triggering the rule engine. A token with both high story counts and an active thesis has cleared two independent evidence bars. Stories alone can be smoke without fire. A thesis alone could be aging. Both together mean the agent is seeing ongoing activity AND has formed a view about what it means.\n\nThis is the screener\'s most powerful compound signal. No other crypto screener can surface this combination because no other screener runs a continuous AI agent that generates both raw observations and structured conclusions across a full token universe.',
    who: 'Any trader or researcher who wants the strongest compound signal the screener can produce.',
  },
  {
    title: 'Sector rotation scan',
    goal: 'Which sector is generating the most narrative activity today?',
    problem: 'You think about the market in terms of sectors — DeFi, L1s, L2s, Meme, Gaming — and you want to know which sector is heating up before it is obvious in price charts. Price-based sector rotation is a lagging signal. You want to see where on-chain narrative activity is concentrating.',
    steps: '1. Set the Trend picker to "24H."\n2. Use the "All Categories" dropdown to select a sector — start with DeFi.\n3. Sort by Stories descending.\n4. Note the story counts on the top 5–10 tokens. How many have non-zero counts? How high are the highest counts?\n5. Switch to another sector (e.g. "L2") and repeat. Compare the top-end story counts across sectors.\n6. The sector where the most tokens have the highest story counts is seeing the most on-chain narrative attention. This is where the agent\'s observation pipeline is generating the densest output.',
    why: 'On-chain story activity often precedes price movement because the events the agent detects — whale accumulations, cluster formations, liquidity shifts — are the mechanical causes of price changes, not the consequences. When one sector shows significantly higher story density than others, it means on-chain actors are concentrating their activity there. This is not a social-media sentiment signal. It is a structural signal derived from actual on-chain events.\n\nThis is like checking which neighborhoods have the most lights switching on at night before the rest of the city notices a crowd forming. The activity is real, physical, and happening before the price charts reflect it.',
    who: 'Macro-oriented traders watching for sector rotation. Fund managers deciding where to allocate attention. Anyone who wants a narrative-driven view of where the market\'s energy is concentrating.',
  },
  {
    title: 'Hidden gem discovery',
    goal: 'Find small-cap tokens the agent is already watching.',
    problem: 'Most attention in crypto concentrates on the top 100–200 tokens by market cap. But the agent covers 7,000+ tokens with no size bias — it observes on-chain behavior for a $5M micro-cap with the same thoroughness as a $5B blue chip. You want to find the small tokens where the agent sees meaningful activity that almost no one else is watching.',
    steps: '1. Open the Screener panel in the toolbar.\n2. Set a maximum MCap — for example, $50M to focus on micro-cap, or $200M for small-cap.\n3. Set the Trend picker to "24H."\n4. Sort by Stories descending.\n5. The tokens at the top are small-cap tokens with active agent-generated stories in the last 24 hours. These are the tokens where on-chain activity is happening but market attention is likely low.\n6. Click through to read the stories. Pay particular attention to story types: WHALE accumulation or SMART_MONEY convergence on a small-cap token is a fundamentally different signal from a HOTLINKS spike (which could just be organic micro-cap volatility).',
    why: 'The agent runs the same detection pipeline on every token in its universe — it does not allocate more compute to large caps or popular tokens. This means a micro-cap token experiencing genuine whale accumulation, cluster formation, or breakout confirmation will show up in the Stories column just as clearly as if it were Ethereum. The market\'s attention is concentrated at the top; the agent\'s attention is distributed across the entire universe.\n\nThis is the equivalent of sweeping a metal detector across the whole beach instead of standing where everyone else is already digging. The edge is coverage breadth. The small-cap tokens with active stories are the ones where you have the best chance of seeing something before the crowd does.',
    who: 'Micro-cap specialists looking for early signals. Research analysts screening for overlooked tokens with structural on-chain activity. Anyone who believes edge comes from coverage rather than consensus.',
  },
  {
    title: 'Fragility + narrative cross-check',
    goal: 'Find tokens with high story activity but high fragility — a risk warning.',
    problem: 'A token with a lot of story activity looks interesting. But what if the data underlying that token is unreliable? What if the price comes from a single thin-liquidity pair, the MCap is estimated from unverified supply, and a small trade could move the market by 10%? The stories might be real, but the numbers surrounding them could be misleading. You want to cross-reference narrative activity with structural risk.',
    steps: '1. Set the Trend picker to "24H."\n2. Sort by Stories descending to find the tokens with the most narrative activity.\n3. Now scan the Fragility column on those top results. Focus on tokens with Fragility scores of 60 or higher.\n4. High fragility combined with high story count is a warning sign. The on-chain activity the agent is observing might be real, but the token\'s data infrastructure — its price, MCap, volume, and liquidity figures — cannot be fully trusted.\n5. For high-fragility tokens with interesting stories: click through and read the stories carefully. Look specifically for LIQUIDITY_DRAIN, WASH_TRADE, LOOP, or RUG_LIQUIDITY_PULL story types, which often co-occur with high fragility.',
    why: 'Stories are generated from on-chain events without filtering for data quality — the agent reports what it sees. Fragility adds a separate, structural overlay that measures how reliable the token\'s market data actually is. A token can have genuine whale accumulation stories and also have a fragility score of 80, meaning the price data is sketchy and the MCap might be off by an order of magnitude. Using both signals together lets you separate "interesting and trustworthy" from "interesting but dangerous to act on."\n\nThink of it as the stress test applied to the dashboard itself. A bridge can look busy — cars crossing, pedestrians walking — but if the bridge is swaying under load, that structural information matters just as much as the traffic count. Fragility is the sway measurement.',
    who: 'Risk-conscious traders who want to validate data quality before acting. Fund managers with mandates that require structural due diligence. Anyone who knows that in crypto, not all data is created equal.',
  },
];

export default function PriceUseCasePage() {
  const classes = useStyles();
  const navigate = useNavigate();

  return (
    <div className={classes.root}>
      <div className={classes.hero}>
        <div className={classes.titleRow}>
          <div>
            <div className={classes.title}>Price Page Use Cases</div>
            <div className={classes.subtitle}>
              Real workflows for using the E3D screener to find tokens with active AI narratives, fresh investment theses, and on-chain edge — with step-by-step instructions.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/price-guide')}>
              User Guide
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/price-reference')}>
              Reference
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/stories')}>
              Stories
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/thesis')}>
              Theses
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/price')}>
              Back to Prices
            </Button>
          </div>
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>How to use this page</div>
        <div className={classes.body}>
          {'The /price page is a scanner with more controls than a typical screener \u2014 Trend intervals, story counts, thesis counts, fragility scores, sector filters, and numeric screeners \u2014 and the interactions between those controls are where the real power lives. These use cases show you exactly which knobs to turn and in what order, depending on what you are trying to find.\n\nEach use case is self-contained: a specific goal, the exact steps, and an explanation of why the approach works. Start with whichever one matches your current question. Over time, you will chain these together into your own compound workflows.'}
        </div>
      </div>

      {useCases.map((item) => (
        <div className={classes.section} key={item.title}>
          <div className={classes.sectionTitle}>{item.title}</div>
          <div className={classes.step}>
            <div className={classes.stepTitle}>Goal</div>
            <div className={classes.body}>{item.goal}</div>
          </div>
          <div className={classes.note} style={{ marginTop: 0, marginBottom: 10 }}>{'The Problem: ' + item.problem}</div>
          <div className={classes.step}>
            <div className={classes.stepTitle}>How to do it</div>
            <div className={classes.body}>{item.steps}</div>
          </div>
          <div className={classes.callout}>{'Why this works: ' + item.why}</div>
          <div className={classes.body} style={{ marginTop: 8, fontStyle: 'italic', color: '#aab4d0' }}>{'Who uses this: ' + item.who}</div>
        </div>
      ))}

      <div className={classes.section}>
        <div className={classes.sectionTitle}>Final takeaway</div>
        <div className={classes.body}>
          {'The best way to use the price page is to pair the live signal layer with the structural risk layer. Stories tell you where the action is. Theses tell you what the agent thinks it means. Fragility tells you whether the setup is solid enough to trust. The Trend picker controls which time window all three layers reflect.'}
        </div>
        <Divider className={classes.divider} />
        <div className={classes.body}>
          {'If you remember one thing, remember this: the price page is the dashboard, Stories are the field notes, Theses are the analyst\'s memo, Fragility is the stress gauge, and the Trend picker is the time lens that controls them all.'}
        </div>
      </div>
    </div>
  );
}
